// Adress-Verifikation via Nominatim (OpenStreetMap). Uebernimmt das Fetch-Muster
// aus ProjectSetupWizard.handleGeocode: gleicher Endpunkt, gleicher User-Agent.
// Rein informativ (non-blocking) – liefert den gefundenen display_name oder null.

export interface AddressParts {
  street?: string;
  zip?: string;
  city?: string;
}

/**
 * Geocodiert eine Adresse ueber Nominatim und gibt den gefundenen display_name
 * zurueck (oder null, wenn nichts Eindeutiges gefunden wurde bzw. bei Fehler).
 * Blockiert nie den Speichervorgang – Fehler werden abgefangen.
 */
export const verifyAddress = async (parts: AddressParts): Promise<string | null> => {
  const { street = '', zip = '', city = '' } = parts;
  if (!street.trim() && !zip.trim() && !city.trim()) return null;
  const query = `${street}, ${zip} ${city}`.trim();
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
    const res = await fetch(url, { headers: { 'User-Agent': 'SiGeKo-App' } });
    const data = await res.json();
    if (data && data.length > 0 && data[0]?.display_name) {
      return String(data[0].display_name);
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Best-effort-Parser fuer eine einzeilige Alt-Adresse ("Straße, PLZ Ort" bzw.
 * "Straße, PLZ Ort" mit Zeilenumbruechen) in Straße / PLZ / Ort.
 * Heuristik: letzte 5-stellige Zahl = PLZ, davor = Straße, danach = Ort.
 */
export const parseLegacyAddress = (raw: string): AddressParts => {
  const text = (raw || '').replace(/\s+/g, ' ').trim();
  if (!text) return {};
  // PLZ (5-stellig) im Text suchen – deutsches Format.
  const zipMatch = text.match(/\b(\d{5})\b/);
  if (zipMatch) {
    const zip = zipMatch[1];
    const idx = text.indexOf(zip);
    const before = text.slice(0, idx).replace(/[,\s]+$/, '').trim();
    const after = text.slice(idx + zip.length).replace(/^[,\s]+/, '').trim();
    return {
      street: before || undefined,
      zip,
      city: after || undefined,
    };
  }
  // Kein PLZ: an Komma trennen (Straße, Ort) oder alles als Straße.
  const commaParts = text.split(',').map((p) => p.trim()).filter(Boolean);
  if (commaParts.length >= 2) {
    return { street: commaParts[0], city: commaParts.slice(1).join(', ') };
  }
  return { street: text };
};
